import { useEffect, useRef, useState } from 'react';
import { Upload, X, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import {
  MEDIA_ACCEPT,
  IMAGE_ACCEPT,
  uploadZernioMedia,
  validateMediaFile,
  mediaAllowanceHint,
  mediaSelectionError,
  remainingMediaSlots,
  mediaCapabilityFor,
  type PostType,
} from '@/services/zernio.service';
import { probeVideoFile } from '@/services/marketing.service';
import type { PickedMedia } from './ZernioMediaPicker';

/** Pixel size of a local image, for the carousel shape rule. Null when unreadable. */
function probeImageFile(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const done = (r: { width: number; height: number } | null) => {
      URL.revokeObjectURL(url);
      resolve(r);
    };
    img.onload = () => done(img.naturalWidth && img.naturalHeight ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    img.onerror = () => done(null);
    img.src = url;
  });
}

/**
 * The composer's MULTI-media picker: up to 10 items, previews, per-item removal
 * and reordering.
 *
 * Shared by the New Post composer and the template editor, so a template can
 * hold exactly what a post can. The template editor passes no platforms: a
 * template has no destination yet, so only the rules every post shares apply
 * until accounts are chosen in the composer. Separate from ZernioMediaPicker,
 * the single-item picker; sharing the PickedMedia shape keeps the two
 * interchangeable at the call site.
 *
 * ── How the ceiling works ──
 * Everything comes from the selected platforms, via zernio.service:
 * `remainingMediaSlots` decides how many more files the input will accept, and
 * `mediaSelectionError` produces the sentence shown when a selection cannot be
 * published. Both are per media TYPE — TikTok takes 10 photos or 1 video and
 * refuses to mix them — so the uploader stops offering images once a video is
 * in, and vice versa.
 *
 * Order is the array order, which is what reaches Zernio as `mediaItems`. Zernio
 * documents no ordering guarantee, but an ordered JSON array is honoured in
 * practice; if that ever proves wrong, the reorder controls are the thing to
 * remove, not the array.
 */
interface Props {
  value: PickedMedia[];
  onChange: (media: PickedMedia[]) => void;
  /** Platforms of the currently selected accounts — drives every limit here. */
  platforms: Array<string | null>;
  /** Feed unless the composer offers a choice — changes every media rule below. */
  postType?: PostType;
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
  /** Wording for the rules when no account is chosen: "A template can…" vs "A post can…". */
  noun?: 'post' | 'template';
}

export function ZernioMediaListPicker({
  value,
  onChange,
  platforms,
  postType = 'feed',
  onUploadingChange,
  disabled,
  noun = 'post',
}: Props) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  /** The file being sent right now: how far along, and when it started (for time left). */
  const [transfer, setTransfer] = useState<{
    name: string; percent: number; loaded: number; size: number; startedAt: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cap = mediaCapabilityFor(platforms, postType);
  const remaining = remainingMediaSlots(value, platforms);
  const limitError = mediaSelectionError(value, platforms, postType, noun);
  const full = remaining <= 0;
  // Once the selected accounts take no more videos (one, for everything but an
  // Instagram carousel), the file browser only offers images.
  const videoCount = value.filter((m) => m.type === 'video').length;
  const videosFull = videoCount > 0 && videoCount >= cap.maxVideos;
  const maxVideoMb = Math.round(cap.maxVideoBytes / 1048576);

  useEffect(() => onUploadingChange?.(busy), [busy, onUploadingChange]);

  /**
   * Upload a batch. Sequential rather than parallel: a large video already
   * saturates the link, and ten concurrent presigned PUTs make per-file progress
   * meaningless while risking throttling at the bucket.
   */
  async function addFiles(files: File[]) {
    if (disabled || files.length === 0) return;
    setError(null);

    // Trim to what the platforms still allow, and say so rather than dropping
    // the extras silently.
    let batch = files;
    if (files.length > remaining) {
      batch = files.slice(0, remaining);
      setError(
        remaining === 0
          ? 'No more media can be added for the selected accounts.'
          : `Only ${remaining} more item${remaining === 1 ? '' : 's'} can be added for the selected accounts — the rest were skipped.`,
      );
    }

    const rejected = batch.map(validateMediaFile).find(Boolean);
    if (rejected) {
      setError(rejected);
      return;
    }

    // The per-type rules, checked BEFORE uploading — the total count above does
    // not stop two videos, or photos alongside a video. A file that would break
    // them is left out, and the message says why, so nothing is uploaded only to
    // be flagged afterwards.
    const kept: File[] = [];
    let skipReason: string | null = null;
    for (const file of batch) {
      const type: 'image' | 'video' = file.type.startsWith('video/') || /\.(mp4|mov)$/i.test(file.name) ? 'video' : 'image';
      const trial = [...value, ...kept.map((f) => ({
        type: (f.type.startsWith('video/') || /\.(mp4|mov)$/i.test(f.name) ? 'video' : 'image') as 'image' | 'video',
      })), { type }];
      const problem = mediaSelectionError(trial, platforms, postType, noun);
      if (problem) {
        skipReason ??= problem;
        continue;
      }
      kept.push(file);
    }
    if (skipReason) {
      // Several videos picked at once, where only one is allowed, is the common
      // case: say it plainly.
      const pickedVideos = batch.filter((f) => f.type.startsWith('video/') || /\.(mp4|mov)$/i.test(f.name)).length;
      const extraVideos = cap.maxVideos === 1
        && kept.some((f) => f.type.startsWith('video/') || /\.(mp4|mov)$/i.test(f.name)) && pickedVideos > 1;
      setError(extraVideos
        ? `Only one video can be added, so the other ${pickedVideos - 1 === 1 ? 'video was' : `${pickedVideos - 1} videos were`} skipped.`
        : kept.length > 0 ? `Some files were skipped. ${skipReason}` : skipReason);
    }
    if (kept.length === 0) return;
    batch = kept;

    setBusy(true);
    setProgress({ done: 0, total: batch.length });
    const added: PickedMedia[] = [];
    let measuredSkip: string | null = null;
    try {
      for (const [i, file] of batch.entries()) {
        // Measured BEFORE the upload: Zernio's upload response carries no
        // duration, and the size, length and shape rules (9:16 for Stories and
        // Reels, one shape per Instagram carousel) need the figures to refuse a
        // file before it spends minutes uploading. It reads the local file, so
        // it never waits on the network.
        const isVideo = file.type.startsWith('video/') || /\.(mp4|mov)$/i.test(file.name);
        const videoProbe = isVideo ? await probeVideoFile(file).catch(() => null) : null;
        const probe = isVideo ? videoProbe : await probeImageFile(file);
        const durationSeconds: number | undefined = videoProbe?.duration;
        const problem = mediaSelectionError(
          [...value, ...added, {
            type: isVideo ? 'video' : 'image',
            name: file.name,
            bytes: file.size,
            durationSeconds,
            width: probe?.width,
            height: probe?.height,
          }],
          platforms,
          postType,
          noun,
        );
        if (problem) {
          measuredSkip ??= `"${file.name}" was skipped. ${problem}`;
          setProgress({ done: i + 1, total: batch.length });
          continue;
        }

        const startedAt = Date.now();
        setTransfer({ name: file.name, percent: 0, loaded: 0, size: file.size, startedAt });
        const media = await uploadZernioMedia(file, (percent, loaded, total) =>
          setTransfer({ name: file.name, percent, loaded, size: total || file.size, startedAt }));
        added.push({
          url: media.url,
          ...(media.previewUrl ? { previewUrl: media.previewUrl } : {}),
          type: media.type,
          name: file.name,
          bytes: media.bytes,
          durationSeconds,
          width: probe?.width,
          height: probe?.height,
        });
        setProgress({ done: i + 1, total: batch.length });
      }
      if (measuredSkip) setError(measuredSkip);
      if (added.length > 0) onChange([...value, ...added]);
    } catch (e) {
      // Keep whatever did upload — re-picking ten files because the eighth
      // failed is worse than a partial batch plus a clear message.
      if (added.length > 0) onChange([...value, ...added]);
      setError(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Upload failed. Please try again.',
      );
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0 });
      setTransfer(null);
    }
  }

  const move = (from: number, to: number) => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const remove = (i: number) => {
    setError(null);
    onChange(value.filter((_, n) => n !== i));
  };

  return (
    <div>
      {/* ── Selected items ─────────────────────────────────────────────────── */}
      {value.length > 0 && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">
              {value.length} / {cap.maxTotal} media items
            </span>
            {value.length > 1 && (
              <span className="text-xs text-slate-400">First item leads the post</span>
            )}
          </div>

          <ul className="mb-3 flex flex-wrap gap-2">
            {value.map((m, i) => (
              <li
                key={`${m.url}-${i}`}
                className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-900"
              >
                {m.type === 'video' ? (
                  <video src={m.previewUrl ?? m.url} className="h-full w-full object-cover" preload="metadata" muted />
                ) : (
                  <img src={m.previewUrl ?? m.url} alt={m.name} className="h-full w-full object-cover" />
                )}

                {/* Position badge — the order that reaches Zernio. */}
                <span className="absolute left-0.5 top-0.5 rounded bg-black/60 px-1 text-[10px] font-medium text-white">
                  {i + 1}
                </span>

                <button
                  type="button"
                  aria-label={`Remove ${m.name}`}
                  disabled={disabled || busy}
                  onClick={() => remove(i)}
                  className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white hover:bg-black/80 disabled:cursor-not-allowed"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>

                {value.length > 1 && (
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50">
                    <button
                      type="button"
                      aria-label={`Move ${m.name} earlier`}
                      disabled={disabled || busy || i === 0}
                      onClick={() => move(i, i - 1)}
                      className="p-0.5 text-white disabled:opacity-30"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${m.name} later`}
                      disabled={disabled || busy || i === value.length - 1}
                      onClick={() => move(i, i + 1)}
                      className="p-0.5 text-white disabled:opacity-30"
                    >
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ── Drop zone ──────────────────────────────────────────────────────── */}
      {!full && (
        <div
          role="button"
          tabIndex={disabled || busy ? -1 : 0}
          aria-disabled={disabled || busy}
          onClick={() => !disabled && !busy && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && !busy && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void addFiles(Array.from(e.dataTransfer.files ?? []));
          }}
          className={cn(
            'flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-6 py-5 text-center transition-colors duration-150',
            dragging
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100',
            busy ? 'cursor-wait' : 'cursor-pointer',
            disabled && 'cursor-not-allowed opacity-50',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          )}
        >
          {busy ? (
            <UploadProgressBar transfer={transfer} index={progress.done} count={progress.total} />
          ) : (
            <>
              <Upload className="h-6 w-6 text-slate-400" aria-hidden="true" />
              <p className="text-sm font-medium text-slate-700">
                Drop {remaining > 1 ? 'images or videos' : 'an image or video'} here or{' '}
                <span className="text-blue-600">click to browse</span>
              </p>
              <p className="text-xs text-slate-400">
                JPEG, PNG, WebP up to 20 MB · MP4, MOV up to {maxVideoMb} MB
              </p>
            </>
          )}
        </div>
      )}

      {videosFull && (
        <p className="mt-2 text-xs text-slate-500">
          {cap.maxVideos === 1
            ? <>This {noun} has a video. Remove it to add a different one — the selected accounts take only one video per {noun}.</>
            : <>This {noun} has the most videos the selected accounts allow ({cap.maxVideos}).</>}
        </p>
      )}

      {/* ── Guidance and validation ────────────────────────────────────────── */}
      {limitError ? (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-red-600">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {limitError}
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-500">{mediaAllowanceHint(platforms)}</p>
      )}
      {error && <p className="mt-1 text-xs font-medium text-amber-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        // With a video already in, a second can't be chosen — only photos, and
        // only where the chosen accounts allow photos beside a video at all.
        accept={videosFull ? IMAGE_ACCEPT : MEDIA_ACCEPT}
        multiple
        disabled={disabled || busy}
        onChange={(e) => {
          void addFiles(Array.from(e.target.files ?? []));
          // Reset so re-picking the same files still fires a change.
          e.target.value = '';
        }}
        className="sr-only"
        aria-hidden="true"
      />
    </div>
  );
}

/** "12.4 MB" */
function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "about 20s left", "about 2 min left" — from the average speed so far. */
function timeLeft(loaded: number, size: number, startedAt: number): string | null {
  const elapsed = (Date.now() - startedAt) / 1000;
  // Too early to say anything honest: the first second is mostly setup.
  if (loaded <= 0 || elapsed < 1.5) return null;
  const seconds = Math.max(0, (size - loaded) / (loaded / elapsed));
  if (seconds < 5) return 'a few seconds left';
  if (seconds < 60) return `about ${Math.round(seconds / 5) * 5}s left`;
  return `about ${Math.round(seconds / 60)} min left`;
}

/**
 * The live bar while a file uploads: percent, megabytes sent of the total and a
 * rough time left. At 100% the bytes are there and the server is checking and
 * filing the file, which takes a moment — said as "Finishing…" rather than
 * leaving a full bar that seems stuck.
 */
function UploadProgressBar({
  transfer, index, count,
}: {
  transfer: { name: string; percent: number; loaded: number; size: number; startedAt: number } | null;
  index: number;
  count: number;
}) {
  const percent = transfer?.percent ?? 0;
  const finishing = percent >= 100;
  const eta = transfer && !finishing ? timeLeft(transfer.loaded, transfer.size, transfer.startedAt) : null;

  return (
    <div className="w-full max-w-md space-y-2" role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-slate-700 truncate">
          {count > 1 ? `Uploading ${index + 1} of ${count}` : 'Uploading'}
          {transfer ? ` · ${transfer.name}` : '…'}
        </span>
        <span className="shrink-0 tabular-nums text-slate-600">{finishing ? '100%' : `${percent}%`}</span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div
          className={cn('h-full rounded-full bg-blue-600 transition-[width] duration-300', finishing && 'animate-pulse')}
          style={{ width: `${Math.max(2, percent)}%` }}
        />
      </div>
      <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
        {finishing ? (
          <><Spinner className="h-3 w-3" /> Finishing…</>
        ) : transfer ? (
          <>{mb(transfer.loaded)} of {mb(transfer.size)}{eta ? ` · ${eta}` : ''}</>
        ) : (
          'Starting…'
        )}
      </p>
    </div>
  );
}
